import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@images/svg-icons/closeIcon.svg';
import ErrorIcon from '@images/svg-icons/errorIcon.svg';
import SuccessIcon from '@images/svg-icons/successIcon.svg';
import { InputAdornment, TextField } from '@mui/material';
import { replaceLabelPlaceholder } from '@utils/pages-utils/EyeExamsUtils';
import { getLabelFallback } from '@utils/utils';
import { SyntheticEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import * as yup from 'yup';

interface Form {
    phone: string;
}

type SearchDoctorByPhoneProps = {
    getDoctor;
};

const schema = yup.object().shape({
    phone: yup
        .string()
        .required(
            replaceLabelPlaceholder(
                'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                '{FORM_FIELD}',
                getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_PHONENUMBER1_LABEL')
            )
        )
        .test(
            'len',
            getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_INVALID_PHONENUMBER1'),
            val => (val ? val.replace(/\D/g, '').length >= 10 : true)
        ),
});

const SearchDoctorByPhone = (props: SearchDoctorByPhoneProps) => {
    const defaultFormValues: Form = {
        phone: '',
    };

    const initialFormStatus = {
        phone: {
            focus: false,
            success: false,
        },
    };
    const [formStatus, setFormStatus] = useState(initialFormStatus);

    const methods = useForm<Form>({
        resolver: yupResolver(schema),
        mode: 'onBlur',
        defaultValues: defaultFormValues,
    });

    const {
        register,
        handleSubmit,
        setValue,
        clearErrors,
        setError,
        watch,
        formState: { errors, isSubmitting },
    } = methods;

    const watchPhone = watch('phone');

    const handleInputEvent = (e: SyntheticEvent) => {
        e.persist();
        const event = e.target as HTMLInputElement;

        if (e.type === 'focus') {
            clearErrors(event.name as keyof Form);
            setFormStatus({
                ...formStatus,
                [event.name]: { success: event.value.replace(/\D/g, '').length >= 10, focus: true },
            });
        }

        if (e.type === 'blur') {
            setFormStatus({
                ...formStatus,
                [event.name]: {
                    success: event.value.replace(/\D/g, '').length >= 10,
                    focus: false,
                },
            });
            setValue(event.name as keyof Form, event.value.trim());
            if (!event.value.trim()) {
                setError(event.name as keyof Form, {
                    type: 'required',
                    message: getLabelFallback(
                        'MYPROFILE_MY_PRESCRIPTIONS_GENERIC_FIELD_CANNOT_BE_EMPTY'
                    ),
                });
            }
        }
    };

    const submit = () => {
        try {
            schema.validateSync({
                phone: watchPhone,
            });
            props.getDoctor(watchPhone, null, null, null);
        } catch {
            setError('phone', {
                type: 'test',
                message: getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_INVALID_PHONENUMBER1'),
            });
        }
    };

    return (
        <div className="search-doctor-modal_by-phone">
            <div className="form-group">
                <NumberFormat
                    {...register('phone')}
                    format={'(###) ###-####'}
                    customInput={TextField}
                    className={`${
                        errors?.phone?.message
                            ? 'form-field-error'
                            : formStatus.phone.success
                            ? 'form-field-success'
                            : ''
                    }`}
                    value={watchPhone}
                    onChange={handleInputEvent}
                    onFocus={handleInputEvent}
                    onBlur={handleInputEvent}
                    name="phone"
                    variant="outlined"
                    label={'Doctor phone number'}
                    InputProps={{
                        endAdornment: formStatus.phone.focus ? (
                            <InputAdornment position="end">
                                <CloseIcon
                                    className="cursor-pointer"
                                    onMouseDown={() => setValue('phone', '')}
                                />
                            </InputAdornment>
                        ) : errors?.phone?.message ? (
                            <InputAdornment position="end">
                                <ErrorIcon />
                            </InputAdornment>
                        ) : formStatus.phone.success ? (
                            <InputAdornment position="end">
                                <SuccessIcon />
                            </InputAdornment>
                        ) : null,
                    }}
                />
            </div>

            <div className="search-doctor-modal_submit">
                <button
                    className="primary-button button-size-medium"
                    onClick={e => {
                        e.preventDefault();
                        submit();
                    }}
                >
                    Submit
                </button>
            </div>
        </div>
    );
};

export default SearchDoctorByPhone;
